import { useCallback, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import PropTypes from 'prop-types'
import { Drawer, Tabs, Splitter, Descriptions } from 'antd'
import { StarOutlined } from '@ant-design/icons'
import humps from 'humps'
import dayjs from 'dayjs'
import { useAsync } from '../../hooks'
import { ListError } from '../Common'
import PDFPreview from '../Common/PDFPreview'
import api from '../../api'
import Loading from '../Loading'
import ReportParsingIssuesForm from './ReportParsingIssuesForm'
import ReportPatientHeader from './ReportPatientHeader'
import Form from './Form'
import TransmissionSidebar from '../Common/TransmissionSidebar'
import { useSidebar } from '../Layout/SidebarContext'

const { TabPane } = Tabs

const useStyles = createUseStyles({
  app: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: '1 1 auto',
  },
  pdfBox: {
    maxWidth: 'calc(100% - 456px)',
    flexGrow: '1',
    minWidth: '100%',
    position: 'relative',
    height: 'calc(100vh - 96px - 68px)',
    overflowY: 'scroll',
    overflowX: 'hidden',
    '& canvas': {
      maxWidth: '100%',
    },
  },
  formBox: {
    position: 'relative',
    overflowY: 'scroll',
    height: 'calc(100vh - 96px - 68px)',
    padding: '0 16px',
    '& > form': {
      marginTop: '-16px',
    },
  },
  patientContainer: {
    padding: '0 16px',
   },
})

export default function EditReport({ isV2Report = false }) {
  const { id } = useParams()
  const [, setRefetchTrigger] = useState(0)
  const [issuesFormFields, setIssuesFormFields] = useState({
    errorFields: [],
    observations: '',
  })
  const [issuesFormVisible, setIssuesFormVisible] = useState(false)
  const [issuesFormSubmitted, setIssuesFormSubmitted] = useState(false)
  const [isPatientDrawerVisible, setIsPatientDrawerVisible] = useState(false)
  const { setIsSidebarVisible, isSidebarVisible } = useSidebar()

  const togglePatientDrawer = () => {
    if (!isPatientDrawerVisible && isSidebarVisible) {
      setIsSidebarVisible(false)
    } else if (isPatientDrawerVisible && !isSidebarVisible) {
      setIsSidebarVisible(true)
    }
    setIsPatientDrawerVisible(!isPatientDrawerVisible)
  }

  const getReport = useCallback(
    () => api.reports.getReportForm(id, isV2Report),
    [id, isV2Report],
  )
  const { error, pending: loading, value: reportForm } = useAsync(getReport)

  const classes = useStyles()
  const handleRefetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])
  const navigate = useNavigate()

  const goBack = () => {
    navigate(-1)
  }

  const getRawReport = useCallback(
    () => api.reports.get(id, {}, isV2Report),
    [id, isV2Report],
  )

  const {
    error: rawReportError,
    pending: rawReportLoading,
    value: rawReport,
  } = useAsync(getRawReport)

  const isIssuesFormVisible =
    !issuesFormSubmitted &&
    rawReport?.pdfExtraction?.status === 'complete' &&
    rawReport?.pdfExtraction?.id !== null &&
    !rawReport?.biobridgeReport

  if (loading || rawReportLoading) {
    return <Loading large />
  }

  if (error || rawReportError) {
    return (
      <ListError
        message="There was an error loading this report"
        description={error.message}
      />
    )
  }

  const onIssuesFormSubmit = () => {
    setIssuesFormFields({
      errorFields: [],
      observations: '',
    })
    setIssuesFormVisible(false)
    setIssuesFormSubmitted(true)
  }

  // Toggle one of the autofilled fields.
  const toggleField = field => {
    const key = humps.camelize(field.itemName)
    setIssuesFormFields(prev => ({
      ...prev,
      errorFields: prev.errorFields.includes(key)
        ? prev.errorFields.filter(f => f !== key)
        : [...prev.errorFields, key],
    }))
  }

  const transmissions = rawReport.transmissions?.sort((a, b) => {
    const aMain = a.main ? 1 : 0
    const bMain = b.main ? 1 : 0
    return bMain - aMain // main=true first
  }) || []



  return (
    <div className={classes.app}>
      <ReportPatientHeader
        report={reportForm}
        practiceId={rawReport?.practice?.id}
        cycleId={rawReport?.cycleId}
        backPath={goBack}
        showIssuesForm={isIssuesFormVisible}
        onShowIssuesForm={() => setIssuesFormVisible(true)}
        onPatientClick={togglePatientDrawer}
        isPatientDrawerVisible={isPatientDrawerVisible}
      />
      <div className={classes.content}>
        <Splitter>
          <Splitter.Panel
          >
            <div className={classes.pdfBox}>
              <Tabs className="tabs">
                {transmissions.length > 0
                  ? transmissions.map(t => (
                      <TabPane
                        tab={
                          <span>
                            {dayjs(t.createdAt).format('MMM Do YYYY')}{' '}
                            {t.main && (
                              <StarOutlined
                                style={{ color: 'green', marginLeft: 4 }}
                              />
                            )}
                          </span>
                        }
                        key={`transmission-${t.id}`}
                      >
                        <PDFPreview
                          path={t.fileUrl || t.file_url || '/sample_report.pdf'}
                          showAllPages
                        />
                      </TabPane>
                    ))
                  : reportForm.deviceReport && (
                      <TabPane tab="Transmission" key="transmission">
                        <PDFPreview
                          path={reportForm.deviceReport}
                          showAllPages
                        />
                      </TabPane>
                    )}
              </Tabs>
            </div>
          </Splitter.Panel>
          <Splitter.Panel defaultSize="40%">
            <div className={classes.formBox}>
              <Form
                rawReport={rawReport}
                report={reportForm}
                isV2Report={isV2Report}
                onDataRefresh={handleRefetch}
                issuesFormFields={issuesFormFields}
                onReportField={toggleField}
                showIssuesForm={isIssuesFormVisible}
              />
            </div>
          </Splitter.Panel>
          <Splitter.Panel size={isPatientDrawerVisible ? 300 : 0} resizable={false}>
            <div className={classes.patientContainer}>
              <h3>Patient Information</h3>
              <Descriptions column={1} size="small" layout="vertical">
                <Descriptions.Item label="Device Type">
                  {reportForm.device.deviceModel.deviceType.name}
                </Descriptions.Item>
                <Descriptions.Item label="Device Serial Number">
                  {reportForm.device.serialNumber}
                </Descriptions.Item>
              </Descriptions>
              <TransmissionSidebar
                patient={reportForm.patient}
                practiceId={reportForm.patient.practiceId}
              />
            </div>
          </Splitter.Panel>
        </Splitter>
      </div>
      <Drawer
        width={500}
        title="Report parsing issues"
        placement="right"
        onClose={() => setIssuesFormVisible(false)}
        open={issuesFormVisible}
      >
        <ReportParsingIssuesForm
          report={reportForm}
          rawReport={rawReport}
          initialValues={issuesFormFields}
          onReportField={toggleField}
          onSubmit={onIssuesFormSubmit}
        />
      </Drawer>
    </div>
  )
}

EditReport.propTypes = {
  isV2Report: PropTypes.bool
}
